import {
  getApplicationListV2,
  getServiceApplicationsInServiceCollection,
  getServiceApplicationSubscriptionSettings,
  putServiceApplicationSubscriptionSettings,
  getGroupById,
} from "../../actions/api";
import { getServiceCollection } from "../../actions/api";
import React, { useEffect, useState } from "react";
import { Breadcrumb, Button, Form, Input, Tooltip } from "antd";
import PropTypes from "prop-types";
import { connect, useSelector } from "react-redux";
import { ApplicationHeader, ServiceHeader } from "../headers/PageHeaders";
import { ErrorBar } from "@authzsvc/common-react-components";
import { GroupFormItem, RoleFormItem, selectHasRole } from "./UtilsAdmin";
import { EditOutlined } from "@ant-design/icons";
import { applicationPortal, service_admin_role } from "../../config";
import { HelpApplicationSubscription } from "./help/HelpApplicationSubscription";
import { useParams } from "react-router";
import { NavLink } from "react-router-dom";

const ServiceApplicationSubscriptionSettingsAdmin = ({
  getServiceCollection,
  getApplicationListV2,
  getServiceApplicationsInServiceCollection,
  getServiceApplicationSubscriptionSettings,
  putServiceApplicationSubscriptionSettings,
  serviceCollection,
  serviceApplications,
  applications,
  applicationSettings,
  isLoading,
}) => {
  const { serviceCollectionId, applicationId } = useParams();
  const [serviceCollectionIsAdmin, setServiceCollectionIsAdmin] =
    useState(false);
  const [applicationIsAdmin, setApplicationIsAdmin] = useState(false);
  const isServiceAdmin = useSelector(selectHasRole(service_admin_role));

  const [application, setApplication] = useState(false);
  const [form] = Form.useForm();
  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
  };

  useEffect(() => {
    if (applicationSettings && (serviceCollectionIsAdmin || applicationIsAdmin))
      form.setFieldsValue(applicationSettings);
  }, [form, applicationSettings, serviceCollectionIsAdmin, applicationIsAdmin]);

  useEffect(() => {
    if (application)
      getServiceApplicationSubscriptionSettings(
        application.applicationIdentifier,
      );
  }, [getServiceApplicationSubscriptionSettings, application]);

  useEffect(() => {
    if (serviceApplications) {
      const tmpApp = serviceApplications.find(
        (x) => applicationId === x.applicationIdentifier,
      );
      if (tmpApp) setApplication(tmpApp);
      setApplicationIsAdmin(true);
    }
  }, [serviceApplications, applicationId]);

  useEffect(() => {
    if (serviceCollectionIsAdmin)
      getServiceApplicationsInServiceCollection(serviceCollectionId);
  }, [
    getServiceApplicationsInServiceCollection,
    serviceCollectionIsAdmin,
    serviceCollectionId,
  ]);

  useEffect(() => {
    if (serviceCollection)
      setServiceCollectionIsAdmin(
        applications.some(
          (x) =>
            serviceCollection.applicationIdentifier === x.applicationIdentifier,
        ),
      );
  }, [serviceCollection, applications]);

  useEffect(() => {
    getApplicationListV2();
    getServiceCollection(serviceCollectionId);
  }, [getServiceCollection, serviceCollectionId, getApplicationListV2]);

  const onFinish = async (values) => {
    await putServiceApplicationSubscriptionSettings(applicationId, values);
  };
  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  if (!(serviceCollectionIsAdmin || applicationIsAdmin || isServiceAdmin)) {
    return (
      <ErrorBar
        errors={[
          {
            title: "Access Denied.",
            data: {
              message:
                "You are not administrator of this Service Collection or this Application.",
            },
          },
        ]}
        dismissError={() => {
          window.location.href = "/";
        }}
      />
    );
  } else
    return (
      <div>
        <Breadcrumb
          items={[
            {
              title: <NavLink to="/manage">Service Collections</NavLink>,
            },
            {
              title: (
                <NavLink
                  to={`/manage/${serviceCollection?.applicationIdentifier}`}
                >
                  Service Applications for{" "}
                  {serviceCollection?.applicationIdentifier}
                </NavLink>
              ),
            },
            {
              title: `Applications Subscription Settings for ${application?.applicationIdentifier}`,
            },
          ]}
        />

        <ServiceHeader
          title={serviceCollection?.displayName}
          subTitle={serviceCollection?.description}
          loading={isLoading}
        />

        <ApplicationHeader
          title={application?.displayName}
          subTitle={application?.description}
          loading={isLoading}
          button={
            <Tooltip title="Edit this Application in the Application Portal">
              <Button
                icon={<EditOutlined />}
                href={`${applicationPortal}manage/${application?.applicationIdentifier}`}
                target="_blank"
              />
            </Tooltip>
          }
        />

        <Form
          {...layout}
          form={form}
          initialValues={applicationSettings}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          style={{ maxWidth: 600, marginTop: "10px" }}
        >
          <Form.Item label="Settings Name" name="name">
            <Input disabled />
          </Form.Item>
          <Form.Item label="ApplicationId" name="applicationId">
            <Input disabled />
          </Form.Item>

          <RoleFormItem
            form={form}
            label="Subscribed Role"
            name="subscribedRoleId"
            searchLabel="Select role used for Subscribed people"
            applicationId={applicationId}
            required={true}
          />
          <GroupFormItem
            form={form}
            label="Self Subscription Group"
            name="selfSubscriptionGroupId"
            searchLabel="Select group used for Self Subscription (and mapped to Subscribed Role defined above)"
            tooltip="If set the subscribe button will self subcribe the user to this group. Note: self subscription must be enabled."
            applicationId={applicationId}
            roleId={applicationSettings?.subscribedRoleId}
            required={false}
          />
          <Form.Item
            label="Self Subscription Url"
            name="selfSubscriptionUrl"
            tooltip="If set the subscribe button will go to the specified Url, the unsubscribe button will point to the specified Url suffixed with ?unsubscribe=true"
          >
            <Input placeholder="Self Subscription Url" />
          </Form.Item>
          <RoleFormItem
            form={form}
            label="Denied Role"
            name="deniedRoleId"
            searchLabel="Select role used for Denied people"
            applicationId={applicationId}
          />
          <RoleFormItem
            form={form}
            label="Admin Role"
            name="adminRoleId"
            searchLabel="Select role used for Admin people"
            applicationId={applicationId}
          />

          <Form.Item label="Grace Period Days" name="gracePeriodDays">
            <Input placeholder="Number of days" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              Save
            </Button>
          </Form.Item>
        </Form>

        <HelpApplicationSubscription />
      </div>
    );
};

ServiceApplicationSubscriptionSettingsAdmin.propTypes = {
  getServiceApplicationsInServiceCollection: PropTypes.func.isRequired,
  getServiceCollection: PropTypes.func.isRequired,
  getApplicationListV2: PropTypes.func.isRequired,
  getServiceApplicationSubscriptionSettings: PropTypes.func.isRequired,
  putServiceApplicationSubscriptionSettings: PropTypes.func.isRequired,
  serviceApplications: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  serviceCollection: PropTypes.object,
  applications: PropTypes.array,
  applicationSettings: PropTypes.object,
  getGroupById: PropTypes.func.isRequired,
};

export default connect(
  (state) => ({
    serviceCollection: state.serviceCollection.data,
    applications: state.applications.data,
    serviceApplications: state.serviceApplications.data,
    applicationSettings: state.serviceApplicationSubscriptionSettings.data,
    isLoading: state.serviceApplications.loading,
  }),
  {
    getServiceApplicationsInServiceCollection,
    getServiceCollection,
    getApplicationListV2,
    getServiceApplicationSubscriptionSettings,
    putServiceApplicationSubscriptionSettings,
    getGroupById,
  },
)(ServiceApplicationSubscriptionSettingsAdmin);
